// ============================================================
// TERROR TCG — STARTER DECKS
// All 12 official starter decks mapped to current card IDs
// Each deck: grimoire (Beings + Tomes) + luxmoire (Impera + U'hltis + Affinities)
// ── THE LANES (caP'n, 2026-08-26) ────────────────────────────
//   *"the way the game currently works is correct. Grimoire —
//   Escendants and Tomes only. Luxmoire — everything else that doesn't
//   immediately get set to the board, like Curio and Prophecy."*
// U'hltis and Affinities live in the LUXMOIRE. Curios and Prophecies
// are PRE-PLACED and belong to no deck at all.
//
// ⚠ THE CITATION THIS REPLACES WAS FALSE, thirteen times over in this
// one file: "Rulez v2 (July 2026)". Rulez v2 is the APRIL book, there
// was no July revision of it, and its ch.3 says the OPPOSITE — U'hltis
// and Affinities in the GRIMOIRE, a Luxmoire of Primera only. The
// lanes these decks are built on are the shipping game's, and the
// shipping game is what caP'n has now ruled canon.
// Shared base is in every deck, unique cards build the identity
// ============================================================

// ── SHARED BASE ───────────────────────────────────────────────
// Every starter deck contains this foundation:
// Tomes: 3x Khao Garden, 1x Gauntlets of A'zka, 1x Wall of Light,
//        1x Nothing Left, 1x Blood in Blood Out, 1x Not Here,
//        1x The Devil's Advocate
// Rituals: 1x Seal of Omnis, 1x Seal of Nihil, 1x Soulcerer's Seal

const SHARED_BASE = {
  // Construction
  tome_khao_garden: 3,
  // Treasure
  tome_gauntlets_azka: 1,
  // Nihil
  tome_wall_of_light: 1,
  // Omnia
  tome_nothing_left: 1,
  // Ancient Sorcery
  tome_blood_in_blood_out: 1,
  tome_not_here: 1,
  // Contract
  tome_devils_advocate: 1,
  // Ritual
  tome_seal_of_omnis: 1,
  tome_seal_of_nihil: 1,
  tome_soulcerers_seal: 1,
};

// ⭐ TWELVE COLOURS, TWELVE DECKS (caP'n, 2026-08-22): *"confidence
// V'ael, let's not make anything too similar — that is how slop
// starts."*
//
// Seven of the twelve used to share a colour with another deck.
// #8B2020 was Shadow Fox AND Z4Z AND Chaos Dragon; #4a7c59 was
// Spectral Tiger AND DG-0; #c8a84b was Lucky Dragon AND Premier
// Dragon. `color` is the ONLY visual metadata a starter deck carries —
// it drives the deck box, the onboarding row, the Plaza list and the
// shop tile's top border — so three collisions meant seven decks that
// could not be told apart at a glance anywhere in the game.
//
// ── THE KEY IS `school`, NOT `ilk` (caP'n, 2026-09-14: "school :3") ──
// Space · Phantom · Time · Dragon are SCHOOLS — the TOP-RIGHT stamp.
// The TOP-LEFT stamp is the Ilk (Humanoid · Beast · Machine · Dragon)
// and no starter deck carries one. This field was called `ilk` until
// 2026-09-14; its four readers (Onboarding's picker, progression's
// all-starters achievement, BoosterPack's deck shop and this file's own
// getStarterDeckNames) moved with it in one pass, because a half-rename
// silently collapses progression's two Sets to {undefined}.
//
// All twelve are distinct now and each stays inside its School's family:
// Space runs blue-through-violet, Phantom green-crimson-amber, Time
// ember-pale-hive, Dragon gold-magenta-orange. The one that will look
// like a mistake and is not: PREMIER DRAGON IS MAGENTA. Premier is not
// gold — gold is Lucky's, and it is the whole of Lucky's identity.
// Premier is ROYAL.
export const STARTER_DECKS = [

  // ── DECK 01 — H-01 BLUE GIANT ─────────────────────────────
  {
    id: "starter_h01",
    name: "H-01 Blue Giant",
    description: "A versatile Humanoid/Space deck built around H-01's powerful level 3 Super Nova. Mix of H-01, H-09, and Xulan beings with strong Nihil defense.",
    school: "Space",
    color: "#2f5bd8",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_khao_duhmdumn_champion: 1,
      tome_khao_duhmdumn_lord: 1,
      tome_boots_wing_walking: 1,
      tome_lyt_footed: 1,
      tome_omnis_corruption: 1,
      tome_blood_money: 1,
      // Shadow Gambling
      tome_temptation_manifested: 1,
      // Escendants
      h01_beb: 5,
      h09_beb: 3,
      xulan_beb: 3,
      h01_champion: 2,
      h09_champion: 2,
      xulan_champion: 2,
      h01_lord: 3,
    },
    luxmoire: {
      p_h01_1: 5,
      p_h01_2: 4,
      p_h01_3: 3,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_h01_lord: 1,
      aff_holy: 1,
    },
    esclings: ["escling_space", "escling_space", "escling_space", "escling_space"],
    curios: ["curio_wizards_cap", "curio_spectacles"],
    prophecy: "proph_space_race",
  },

  // ── DECK 02 — H-09 NEUTRON STAR ───────────────────────────
  {
    id: "starter_h09",
    name: "H-09 Neutron Star",
    description: "An aggressive Humanoid/Space deck centered on H-09's overwhelming Lyt power. High attack with Nihil counters and Phoenix Aura protection.",
    school: "Space",
    color: "#19c2b0",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_cleansing_burst: 1,
      tome_phoenix_aura: 1,
      tome_regnant: 1,
      tome_not_now: 1,
      tome_blood_money: 1,
      tome_monoliths_of_nihil: 1,
      // Shadow Gambling
      tome_snake_eyes: 1,
      // Escendants
      h09_beb: 5,
      xulan_beb: 3,
      h01_beb: 3,
      h01_champion: 2,
      h09_champion: 2,
      xulan_champion: 2,
      h09_lord: 3,
    },
    luxmoire: {
      p_h09_1: 5,
      p_h09_2: 3,
      p_h09_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_h09_lord: 1,
      aff_evil: 1,
    },
    esclings: ["escling_space", "escling_space", "escling_space", "escling_space"],
    curios: ["curio_warlocks_robe", "curio_mages_amulet"],
    prophecy: "proph_tablet_nullus",
  },

  // ── DECK 03 — XULAN ───────────────────────────────────────
  {
    id: "starter_xulan",
    name: "Xulan",
    description: "A precise assassin deck built on Xulan's Anima-draining attacks. Enlightened Affinity and control Tomes make this deck a patient predator.",
    school: "Space",
    color: "#7c3aed",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_sword_shield_omnis: 1,
      tome_sword_of_shadows: 1,
      tome_lyt_footed: 1,
      tome_lyt_hearted: 1,
      tome_shower_of_lyt: 1,
      // Shadow Gambling
      tome_temptation_manifested: 1,
      // Escendants
      xulan_beb: 5,
      h09_beb: 3,
      h01_beb: 3,
      h01_champion: 2,
      h09_champion: 2,
      xulan_champion: 2,
      xulan_lord: 1,
    },
    luxmoire: {
      p_xulan_1: 5,
      p_xulan_2: 3,
      p_xulan_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_xulan_lord: 1,
      aff_awoken: 1,
    },
    esclings: ["escling_space", "escling_space", "escling_space", "escling_space"],
    curios: ["curio_warlocks_robe", "curio_circle_of_lyf"],
    prophecy: "proph_bloody_hell",
  },

  // ── DECK 04 — SPECTRAL TIGER ──────────────────────────────
  {
    id: "starter_spectral_tiger",
    name: "Spectral Tiger",
    description: "A Phantom Beast deck that hits Anima directly. Spectral Paws enables early pressure, Haunting Presence U'hlti closes games. Holy Affinity provides clutch protection.",
    school: "Phantom",
    color: "#3fae7a",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_sword_of_shadows: 1,
      tome_lyt_footed: 1,
      tome_omnis_corruption: 1,
      tome_not_now: 1,
      tome_lyt_hearted: 1,
      tome_solomon_godfry: 1,
      // Shadow Gambling
      tome_daft_fantasy: 1,
      // Escendants
      spectral_tiger_beb: 5,
      shadow_fox_beb: 3,
      inverted_serpent_beb: 3,
      inverted_serpent_champion: 2,
      shadow_fox_champion: 2,
      spectral_tiger_champion: 2,
      spectral_tiger_lord: 3,
    },
    luxmoire: {
      p_spectral_tiger_1: 5,
      p_spectral_tiger_2: 3,
      p_spectral_tiger_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_spectral_tiger_lord: 1,
      aff_holy: 1,
    },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom", "escling_phantom"],
    curios: ["curio_beb_buddy", "curio_badass_beb"],
    prophecy: "proph_phantom_planet",
  },

  // ── DECK 05 — SHADOW FOX (Lucky Shadow) ───────────────────
  {
    id: "starter_shadow_fox",
    name: "Lucky Shadow",
    description: "The devious Shadow Fox deck. Intangible threats, direct Anima strikes, Evil Affinity removal, and Underdog's Howl gambling. Mischievous and unpredictable.",
    school: "Phantom",
    color: "#a3123a",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_boots_wing_walking: 1,
      tome_cleansing_burst: 1,
      tome_all_or_nothing: 1,
      tome_regnant: 1,
      tome_not_now: 1,
      tome_sword_of_shadows: 1,
      // Shadow Gambling
      tome_underdogs_howl: 1,
      // Escendants
      shadow_fox_beb: 5,
      inverted_serpent_beb: 3,
      spectral_tiger_beb: 3,
      spectral_tiger_champion: 2,
      shadow_fox_champion: 2,
      inverted_serpent_champion: 2,
      shadow_fox_lord: 3,
    },
    luxmoire: {
      p_shadow_fox_1: 5,
      p_shadow_fox_2: 3,
      p_shadow_fox_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_shadow_fox_lord: 1,
      aff_evil: 1,
    },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom", "escling_phantom"],
    curios: ["curio_beb_buddy", "curio_badass_beb"],
    prophecy: "proph_tablet_nullus",
  },

  // ── DECK 06 — INVERTED SERPENT ────────────────────────────
  {
    id: "starter_inverted_serpent",
    name: "Inverted Serpent",
    description: "A relentless Phantom Beast deck that punishes every attack. Supernatural Bite drains Anima on each Lyt Attack. Inverted Scales punishes Intangible strategies.",
    school: "Phantom",
    color: "#b8701a",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_cleansing_burst: 1,
      tome_lyt_hearted: 1,
      tome_weeping_dagger: 1,
      tome_khao_arcadia: 1,
      tome_sword_of_shadows: 1,
      tome_agree_to_disagree: 1,
      // Shadow Gambling
      tome_goodbye_horses: 1,
      // Escendants
      inverted_serpent_beb: 5,
      inverted_serpent_champion: 2,
      inverted_serpent_lord: 3,
      shadow_fox_beb: 3,
      shadow_fox_champion: 2,
      spectral_tiger_beb: 3,
      spectral_tiger_champion: 2,
    },
    luxmoire: {
      p_inverted_serpent_1: 5,
      p_inverted_serpent_2: 3,
      p_inverted_serpent_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_inverted_serpent_lord: 1,
      aff_awoken: 1,
    },
    esclings: ["escling_phantom", "escling_phantom", "escling_phantom", "escling_phantom"],
    curios: ["curio_elixir", "curio_circle_of_lyf"],
    prophecy: "proph_tablet_omnis",
  },

  // ── DECK 07 — Z4Z ─────────────────────────────────────────
  {
    id: "starter_z4z",
    name: "Z4Z",
    description: "A destructive Time Machine deck. Z4Z demons attack relentlessly, Knights of Khao unleash multi-attacks, Casino Royale guarantees a Shadow Gambling win. Pure aggression.",
    school: "Time",
    color: "#e0431f",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_sword_of_shadows: 1,
      tome_river_of_blood: 1,
      tome_shower_of_lyt: 1,
      tome_lyt_hearted: 1,
      tome_inverted_knife: 1,
      tome_monkey_suite: 1,
      // Shadow Gambling
      tome_casino_royale: 1,
      // Escendants
      z4z_beb: 5,
      z4z_champion: 2,
      z4z_lord: 3,
      dg0_beb: 3,
      dg0_champion: 2,
      antdroid_beb: 3,
      antdroid_champion: 2,
    },
    luxmoire: {
      p_z4z_1: 5,
      p_z4z_2: 3,
      p_z4z_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_z4z_lord: 1,
      aff_evil: 1,
    },
    esclings: ["escling_time", "escling_time", "escling_time", "escling_time"],
    curios: ["curio_warlocks_robe", "curio_elixir"],
    prophecy: "proph_bloody_hell",
  },

  // ── DECK 08 — DG-0 ────────────────────────────────────────
  {
    id: "starter_dg0",
    name: "DG-0",
    description: "A healing and resource Time Machine deck. DG-0 restores Anima while building advantage. Justice Reigns U'hlti controls the board. Stars and Skies fuels massive Anima gains.",
    school: "Time",
    color: "#79b8ec",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_stars_and_skies: 1,
      tome_anima_house: 1,
      tome_necklace_courage: 1,
      tome_lyt_footed: 1,
      tome_regnant: 1,
      tome_monkey_suite: 1,
      // Shadow Gambling
      tome_gambits_lucky_cat: 1,
      // Escendants
      dg0_beb: 5,
      dg0_champion: 2,
      dg0_lord: 3,
      antdroid_beb: 3,
      antdroid_champion: 2,
      z4z_beb: 3,
      z4z_champion: 2,
    },
    luxmoire: {
      p_dg0_1: 5,
      p_dg0_2: 3,
      p_dg0_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_dg0_lord: 1,
      aff_holy: 1,
    },
    esclings: ["escling_time", "escling_time", "escling_time", "escling_time"],
    curios: ["curio_wizards_cap", "curio_mages_amulet"],
    prophecy: "proph_ace_of_shades",
  },

  // ── DECK 09 — ANTDROID ────────────────────────────────────
  {
    id: "starter_antdroid",
    name: "Antdroid",
    description: "A swarming Time Machine deck. The more Antdroids on field the stronger King of the Anthill becomes. King of the Castle generates ongoing Anima tribute. Dragon D'eyes rewards patience.",
    school: "Time",
    color: "#8f6a14",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_khao_arcadia: 1,
      tome_sword_of_shadows: 1,
      tome_shower_of_lyt: 1,
      tome_regnant: 1,
      tome_not_now: 1,
      tome_king_of_the_castle: 1,
      // Shadow Gambling
      tome_dragon_deyes: 1,
      // Escendants
      antdroid_beb: 5,
      antdroid_champion: 2,
      antdroid_lord: 3,
      dg0_beb: 3,
      dg0_champion: 2,
      z4z_beb: 3,
      z4z_champion: 2,
    },
    luxmoire: {
      p_antdroid_1: 5,
      p_antdroid_2: 3,
      p_antdroid_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_antdroid_lord: 1,
      aff_awoken: 1,
    },
    esclings: ["escling_time", "escling_time", "escling_time", "escling_time"],
    curios: ["curio_beb_buddy", "curio_elixir"],
    prophecy: "proph_bloody_hell",
  },

  // ── DECK 10 — LUCKY DRAGON ────────────────────────────────
  {
    id: "starter_lucky_dragon",
    name: "Lucky Dragon",
    description: "The ultimate Shadow Gambling deck. Lucky Dragon SSG Impera stack wins toward Ace of Shades prophecy. Casino Royale, Snake Eyes, Underdog's Howl — fortune favors the bold.",
    school: "Dragon",
    color: "#e8bb3d",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_khao_arcadia: 1,
      tome_lyt_hearted: 1,
      tome_cleansing_burst: 1,
      // Shadow Gambling — heavy stack
      tome_dragon_deyes: 1,
      tome_snake_eyes: 1,
      tome_underdogs_howl: 1,
      tome_casino_royale: 1,
      // Escendants
      lucky_dragon_beb: 5,
      lucky_dragon_champion: 2,
      lucky_dragon_lord: 3,
      premier_dragon_beb: 3,
      premier_dragon_champion: 2,
      chaos_dragon_beb: 3,
      chaos_dragon_champion: 2,
    },
    luxmoire: {
      p_lucky_dragon_1: 5,
      p_lucky_dragon_2: 3,
      p_lucky_dragon_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_lucky_dragon_lord: 1,
      aff_jynxi: 1,
    },
    esclings: ["escling_dragon", "escling_dragon", "escling_dragon", "escling_dragon"],
    curios: ["curio_jynxi_wares", "curio_circle_of_lyf"],
    prophecy: "proph_ace_of_shades",
  },

  // ── DECK 11 — PREMIER DRAGON ──────────────────────────────
  {
    id: "starter_premier_dragon",
    name: "Premier Dragon",
    description: "The noble Dragon deck. Premier Dragon grows stronger with every Dragon on field. Show Stomper U'hlti deals 10 direct damage. Holy Affinity protects the growing army.",
    school: "Dragon",
    color: "#cf3f8f",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_khao_duhmdumn_champion: 1,
      tome_khao_arcadia: 1,
      tome_lyt_footed: 1,
      tome_lyt_hearted: 1,
      tome_agree_to_disagree: 1,
      tome_light_casting_shadow: 1,
      // Shadow Gambling
      tome_dragon_deyes: 1,
      // Escendants
      premier_dragon_beb: 5,
      premier_dragon_champion: 2,
      premier_dragon_lord: 3,
      lucky_dragon_beb: 3,
      lucky_dragon_champion: 2,
      chaos_dragon_beb: 3,
      chaos_dragon_champion: 2,
    },
    luxmoire: {
      p_premier_dragon_1: 5,
      p_premier_dragon_2: 3,
      p_premier_dragon_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_premier_dragon_lord: 1,
      aff_holy: 1,
    },
    esclings: ["escling_dragon", "escling_dragon", "escling_dragon", "escling_dragon"],
    curios: ["curio_wizards_cap", "curio_warlocks_robe"],
    prophecy: "proph_dragonforce",
  },

  // ── DECK 12 — CHAOS DRAGON ────────────────────────────────
  {
    id: "starter_chaos_dragon",
    name: "Chaos Dragon",
    description: "Absolute destruction. Chaos Dragon Bebs hit for 8 Lyt with 0 Lyf — pure glass cannon. None More Than I controls the board. I or None U'hlti ends games instantly. Evil Affinity removes threats.",
    school: "Dragon",
    color: "#ff5f2e",
    grimoire: {
      ...SHARED_BASE,
      // Unique Tomes
      tome_chief_calidum: 1,
      tome_lyt_hearted: 1,
      tome_omnis_corruption: 1,
      tome_shower_of_lyt: 1,
      tome_lyt_footed: 1,
      tome_khao_duhmdumn_champion: 1,
      // Shadow Gambling
      tome_dragon_deyes: 1,
      // Escendants
      chaos_dragon_beb: 5,
      chaos_dragon_champion: 2,
      chaos_dragon_lord: 3,
      premier_dragon_beb: 3,
      premier_dragon_champion: 2,
      lucky_dragon_beb: 3,
      lucky_dragon_champion: 2,
    },
    luxmoire: {
      p_chaos_dragon_1: 5,
      p_chaos_dragon_2: 3,
      p_chaos_dragon_3: 1,
      // THE LANES (R05, caP'n 2026-08-26): U'hltis + Affinities live here
      u_chaos_dragon_lord: 1,
      aff_evil: 1,
    },
    esclings: ["escling_dragon", "escling_dragon", "escling_dragon", "escling_dragon"],
    curios: ["curio_jynxi_wares", "curio_badass_beb"],
    prophecy: "proph_tablet_omnis",
  },

];

// ── Helper: get starter deck by id ───────────────────────────
export const getStarterDeck = (id) =>
  STARTER_DECKS.find(d => d.id === id) || null;

// ── Helper: get all starter deck names ───────────────────────
export const getStarterDeckNames = () =>
  STARTER_DECKS.map(d => ({ id: d.id, name: d.name, school: d.school, color: d.color, description: d.description }));